import { useField } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useMutation } from 'urql';
import { PopupDialogNew } from '~/components/ui/PopupDialogNew';
import { graphql } from '~/gql';
import { ContactInput, ContactType, Status } from '~/gql/graphql';
import { useScrollToFocus } from '~/hooks/useScrollToFocus';
import { useContacts, type Contact } from '~/routes/resources/contacts';
import {
  ShortContactForm,
  ShortContactOutputFormValues,
} from '../contact/ContactForm/ShortContactForm';
import { ContactSelect, ContactSelectProps } from './ContactSelect';

type Props = Omit<ContactSelectProps, 'onCreate'>;
type Ref = HTMLDivElement;

export function selectionAfterCreatingContact(
  current: Contact[],
  created: Contact,
  multiple = false
): Contact[] {
  if (!multiple) {
    return [created];
  }

  if (current.some((contact) => contact.id === created.id)) {
    return current;
  }

  return [...current, created];
}

type FieldProps = Omit<Props, 'value' | 'onChange'> & {
  name: string;
};

const CreateContactDocument = graphql(`
  mutation CreateContact($input: ContactInput!) {
    createContact(input: $input) {
      id
      status
      type
      name
      email
      phone
      relationship
      image
      isCurrentUser
      isTeamMember
    }
  }
`);

function formDataToInput(values: ShortContactOutputFormValues): ContactInput {
  // Note: ShortContactForm now collects UI "types"; here we pass through values.relationship if provided
  // or leave it to the server-side defaults. If ShortContactForm is updated to submit expanded relationships,
  // that array will be used directly.
  return {
    status: Status.Active,
    type: ContactType.Person,
    name: values.name,
    email: values.email,
    images: values.images,
    // If the form provides expanded relationships, pass them; otherwise leave undefined
    relationship: values.relationship ?? undefined,
    phone: values.phone,
    notes: values.notes,
    streetAddress: values.streetAddress
      ? JSON.stringify(values.streetAddress)
      : undefined,
    postalAddress: values.postalAddress
      ? JSON.stringify(values.postalAddress)
      : undefined,
    organisationId:
      values.organisationId.length > 0 ? values.organisationId[0] : undefined,
  };
}

export const FindAddContact = (props: Props) => {
  const contacts = props.contacts ?? useContacts();
  const [{ fetching: isLoading }, createContact] = useMutation(
    CreateContactDocument
  );
  const [showCreateContactPopup, setShowCreateContactPopup] = useState<
    string | false
  >(false);

  const ref = useScrollToFocus(showCreateContactPopup === false);

  async function handleSubmit(data: ShortContactOutputFormValues) {
    const result = await createContact({ input: formDataToInput(data) });

    if (result.error) {
      toast.error('Error adding contact');
      return;
    }

    if (result.data?.createContact) {
      props.onChange(
        selectionAfterCreatingContact(
          props.value,
          result.data.createContact,
          props.multiple
        )
      );
    }
    setShowCreateContactPopup(false);
  }

  return (
    <div ref={ref}>
      <CreateContactPopup
        show={showCreateContactPopup}
        onClose={() => {
          if (isLoading) return;
          setShowCreateContactPopup(false);
        }}
        onSubmit={handleSubmit}
        isLoading={isLoading}
      />
      <ContactSelect
        {...props}
        contacts={contacts}
        onCreate={setShowCreateContactPopup}
      />
    </div>
  );
};

type CreateContactPopupProps = {
  show: string | boolean;
  onClose: () => void;
  onSubmit: (values: ShortContactOutputFormValues) => void;
  isLoading?: boolean;
};

function CreateContactPopup({
  show,
  onClose,
  onSubmit,
  isLoading = false,
}: CreateContactPopupProps) {
  const { t } = useTranslation();
  const defaultName = typeof show === 'string' ? show : undefined;

  return (
    <PopupDialogNew
      title={t('createContact')}
      isOpen={show !== false}
      onClose={onClose}
    >
      <ShortContactForm
        defaultName={defaultName}
        onSubmit={onSubmit}
        emailIsRequired
        isLoading={isLoading}
      />
    </PopupDialogNew>
  );
}

export function FindAddContactField({ name, ...props }: FieldProps) {
  const contacts = useContacts();
  const [field, meta, helpers] = useField(name);

  return (
    <>
      <FindAddContact
        {...props}
        value={
          field.value ? contacts.filter(({ id }) => id === field.value) : []
        }
        onChange={(val) => helpers.setValue(val.length ? val[0].id : null)}
      />
      {meta.error && <p className='ErrorMessage'>{meta.error}</p>}
    </>
  );
}
